/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteAppointments, fetchSiteTaxCalendar } from "@/lib/api";

// A reusable presentation of "what's coming up" for this website, drawn from the
// appointment and tax-calendar data that already exists elsewhere in this dashboard —
// no separate reminders database, so nothing here can ever drift out of sync with the
// agenda/tax-calendar pages themselves.
export default function SiteReminders() {
  const dashboard = useSiteDashboard();
  const { token, slug, capabilities } = dashboard;
  const [pending, setPending] = useState(null);
  const [taxCalendar, setTaxCalendar] = useState(null);

  useEffect(() => {
    if (!token || !capabilities) return;
    if (capabilities.appointments) fetchSiteAppointments(token, slug, { status: "pending", limit: 20 }).then(setPending).catch(() => {});
    if (capabilities.tax_calendar) fetchSiteTaxCalendar(token, slug).then(setTaxCalendar).catch(() => {});
  }, [token, slug, capabilities]);

  const items = [
    ...((pending?.items || []).map((a) => ({ key: a.id, title: `Appointment request from ${a.customer_name}`, date: a.requested_date, to: `/account/websites/${slug}/agenda` }))),
    ...((taxCalendar?.next_deadlines || []).map((d) => ({ key: d.id, title: d.title, date: d.due_date, to: `/account/websites/${slug}/tax-calendar` }))),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <SiteDashboardShell active="reminders" dashboard={dashboard}>
      <h2 className="text-lg font-bold font-display text-[#0F172A] mb-4">Reminders</h2>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
        {items.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">Nothing needs your attention right now.</p>
        ) : (
          items.map((r) => (
            <Link key={r.key} to={r.to} className="flex items-center justify-between gap-3 border-b border-[#E2E8F0] last:border-0 px-4 py-3.5 hover:bg-[#F8FAFC]">
              <span className="text-sm text-[#0F172A]">{r.title}</span>
              <span className="text-xs font-medium text-[#94A3B8]">{r.date}</span>
            </Link>
          ))
        )}
      </div>
    </SiteDashboardShell>
  );
}
